import { CONFIRM_BUTTON_VARIANTS } from 'features/libra/components/confirmButtonVariant'
import {
  getLibraTicketGroupAdminPermission,
  type RolodexPermissionsValues,
  scopeLibraTicketGroupPermission,
} from 'types'
import z from 'zod'

export enum LifecycleTriggerType {
  FIRST_INTERACTION = 'first_interaction',
  STEP_REACHED = 'step_reached',
  WORKFLOW_COMPLETE = 'workflow_complete',
  ACTION = 'action',
}

const firstInteractionTriggerSchema = z.object({
  type: z.literal(LifecycleTriggerType.FIRST_INTERACTION),
})

const stepReachedTriggerSchema = z.object({
  type: z.literal(LifecycleTriggerType.STEP_REACHED),
  stepId: z.string(),
})

const workflowCompleteTriggerSchema = z.object({
  type: z.literal(LifecycleTriggerType.WORKFLOW_COMPLETE),
  label: z.string().optional(),
})

// A bad `style` from a hand-edited rolodex entry must not sink the whole parse
// — `parseLifecycleConfig` would return undefined and the ticket would lose its
// lifecycle entirely.
const lifecycleActionStyleSchema = z
  .enum(CONFIRM_BUTTON_VARIANTS)
  .optional()
  .catch(undefined)

const lifecycleConfirmSchema = z.object({
  message: z.string().default('Are you sure?'),
  confirmLabel: z.string().default('Go Ahead'),
  title: z.string().optional(),
})

/**
 * Requires a free-text justification before the transition runs. `id` names an
 * append-only `JustificationRecord[]` inside the `transition_justifications`
 * field; because each record names its own action, every justified transition
 * on a workflow can share one `id`.
 */
const lifecycleJustifySchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  message: z.string().optional(),
  inputLabel: z.string().default('Justification'),
  placeholder: z.string().optional(),
  minLength: z.number().int().nonnegative().default(0),
  maxLength: z.number().int().positive().default(2000),
  submitLabel: z.string().default('Submit'),
})

const actionTriggerSchema = z.object({
  type: z.literal(LifecycleTriggerType.ACTION),
  label: z.string(),
  roles: z.array(z.string()),
  component: z.string().optional(),
  style: lifecycleActionStyleSchema,
  confirm: lifecycleConfirmSchema.optional(),
  /**
   * Resets sign-off by the same write that moves the stage. Clears each slot's
   * `active` flag only — `signatures` survives (see `deactivateSignoffs`).
   */
  shouldDeactivateSignoffs: z.boolean().optional(),
  /**
   * Blocks this transition until every required sign-off slot is signed. Which
   * slots are required is per-slot (`requiredWhen`); this is only the opt-in.
   */
  requireSignoffs: z.boolean().optional(),
  justify: lifecycleJustifySchema.optional(),
})

const lifecycleTriggerSchema = z.discriminatedUnion('type', [
  firstInteractionTriggerSchema,
  stepReachedTriggerSchema,
  workflowCompleteTriggerSchema,
  actionTriggerSchema,
])

// ---------------------------------------------------------------------------
// Stage & transition schemas
// ---------------------------------------------------------------------------

const lifecycleStageSchema = z.object({
  id: z.string(),
  name: z.string(),
  default: z.boolean().optional(),
  locked: z.boolean().optional(),
  terminal: z.boolean().optional(),
  color: z.string().optional(),
})

const lifecycleTransitionSchema = z.object({
  from: z.string(),
  to: z.string(),
  trigger: lifecycleTriggerSchema,
})

export const lifecycleConfigSchema = z.object({
  stages: z.array(lifecycleStageSchema),
  transitions: z.array(lifecycleTransitionSchema),
})

// ---------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------

export type LifecycleConfig = z.infer<typeof lifecycleConfigSchema>
export type LifecycleStage = z.infer<typeof lifecycleStageSchema>
export type LifecycleTransition = z.infer<typeof lifecycleTransitionSchema>
export type LifecycleTrigger = z.infer<typeof lifecycleTriggerSchema>
export type LifecycleConfirm = z.infer<typeof lifecycleConfirmSchema>
export type LifecycleJustify = z.infer<typeof lifecycleJustifySchema>

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Returns the single automatic transition matching the given trigger type from
 * the current stage, or null if none exists.
 */
export function findMatchingTransition(
  config: LifecycleConfig,
  currentStageId: string,
  trigger:
    | { type: LifecycleTriggerType.FIRST_INTERACTION }
    | { type: LifecycleTriggerType.STEP_REACHED; stepId: string }
    | { type: LifecycleTriggerType.WORKFLOW_COMPLETE }
): LifecycleTransition | null {
  for (const t of config.transitions) {
    if (t.from !== currentStageId) continue
    if (t.trigger.type !== trigger.type) continue
    if (
      trigger.type === LifecycleTriggerType.STEP_REACHED &&
      t.trigger.type === LifecycleTriggerType.STEP_REACHED &&
      t.trigger.stepId !== trigger.stepId
    ) {
      continue
    }
    return t
  }
  return null
}

/**
 * The single authority on what a config-authored `roles` list means for one
 * ticket. `requiredRoles` are permission strings, not `LibraTicketGroupRole`
 * values — a config may name a fully scoped permission or another namespace.
 *
 * The empty-roles case is answered before the missing-group bail, so an
 * ungrouped ticket keeps its open slots available.
 */
export function makeTicketGroupRoleCheck(
  hasPermissions: (perms: RolodexPermissionsValues[]) => boolean,
  ticketGroupId: string | undefined
): (requiredRoles: ReadonlyArray<string>) => boolean {
  return (requiredRoles) => {
    if (
      ticketGroupId &&
      hasPermissions([getLibraTicketGroupAdminPermission(ticketGroupId)])
    )
      return true
    if (requiredRoles.length === 0) return true
    if (!ticketGroupId) return false
    return hasPermissions(
      requiredRoles.map((rolePermission) =>
        scopeLibraTicketGroupPermission(rolePermission, ticketGroupId)
      ) as RolodexPermissionsValues[]
    )
  }
}

/**
 * Action transitions available from the current stage. `userHasRoles` owns the
 * role semantics.
 */
export function getAvailableActions(
  config: LifecycleConfig,
  currentStageId: string,
  userHasRoles: (roles: ReadonlyArray<string>) => boolean
): ReadonlyArray<LifecycleTransition> {
  return config.transitions.filter(
    (t) =>
      t.from === currentStageId &&
      t.trigger.type === LifecycleTriggerType.ACTION &&
      userHasRoles(t.trigger.roles)
  )
}

/**
 * Returns the default stage (the one tickets are created in), falling back
 * to the first stage if none is explicitly marked.
 */
export function getDefaultStage(
  config: LifecycleConfig
): LifecycleStage | undefined {
  return config.stages.find((s) => s.default) ?? config.stages[0] ?? undefined
}

/** A ticket's status if it names a known stage, else the default stage. */
export function getEffectiveStageId(
  ticketStatus: string,
  knownStageIds: ReadonlySet<string>,
  defaultStageId: string | undefined
): string | undefined {
  return knownStageIds.has(ticketStatus) ? ticketStatus : defaultStageId
}

/**
 * Safely parses `globalContext.lifecycleConfig`. Returns the config if valid,
 * or undefined if absent/invalid. Logs validation errors to the console.
 */
export function parseLifecycleConfig(
  globalContext: Record<string, unknown>
): LifecycleConfig | undefined {
  const raw = globalContext.lifecycleConfig
  if (!raw || typeof raw !== 'object') return undefined
  const result = lifecycleConfigSchema.safeParse(raw)
  if (!result.success) {
    console.warn(
      '[Libra] lifecycleConfig failed validation:',
      result.error.issues
    )
    return undefined
  }
  return result.data
}

/**
 * Returns the button label for the workflow-complete action. Checks the
 * current stage first, then falls back to the first `workflow_complete`
 * transition in the config that has a label. This ensures the correct
 * label shows even before the ticket has transitioned out of its default
 * stage (e.g. "Not Started" → "In Progress").
 */
export function getWorkflowCompleteLabel(
  config: LifecycleConfig | undefined,
  currentStageId: string
): string {
  if (!config) return 'Complete'

  const fromCurrent = findMatchingTransition(config, currentStageId, {
    type: LifecycleTriggerType.WORKFLOW_COMPLETE,
  })
  if (
    fromCurrent?.trigger.type === LifecycleTriggerType.WORKFLOW_COMPLETE &&
    fromCurrent.trigger.label
  ) {
    return fromCurrent.trigger.label
  }

  for (const t of config.transitions) {
    if (
      t.trigger.type === LifecycleTriggerType.WORKFLOW_COMPLETE &&
      t.trigger.label
    ) {
      return t.trigger.label
    }
  }

  return 'Complete'
}
